import { useMutation } from '@tanstack/react-query';
import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import { IOperationResultNoValue } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { QuickSightDashboardViewModel } from '../../../viewmodels/AdminViewModels';
import { Button } from '../../../web/components-v2/Button';
import { OpenNewWindowIcon } from '../../../web/components/svgs/icons/OpenNewWindowIcon';
import { styleSheet } from './styles';

interface IProps {
	className?: string;
	dashboardId?: string;
}

export const Report: React.FC<IProps> = ({ dashboardId, className = '' }) => {
	const userSession = useUserSession();

	const [error, setError] = useState<string>(undefined);

	const [url, setUrl] = useState<string>(undefined);

	const openInNewTab = useMutation({
		mutationFn: () => {
			dashboardId ||= userSession.account.isLevitateSalesCoffeeAccount
				? QuickSightDashboardViewModel.AIDARealMagicDialsId
				: QuickSightDashboardViewModel.AIDACustomersDialsId;
			return new QuickSightDashboardViewModel(userSession, dashboardId, null, null).getExternalEmbedUrl();
		},
		onSuccess: embedUrl => {
			window.open(embedUrl, '_blank');
		},
		onError: err => {
			toast.error((err as IOperationResultNoValue)?.systemMessage || 'Could not open the report in a new tab');
		},
	});

	React.useEffect(() => {
		// eslint-disable-next-line react-hooks/exhaustive-deps
		dashboardId ||= userSession.account.isLevitateSalesCoffeeAccount
			? QuickSightDashboardViewModel.AIDARealMagicDialsId
			: QuickSightDashboardViewModel.AIDACustomersDialsId;

		const viewModel = new QuickSightDashboardViewModel(userSession, dashboardId, null, null);
		let isCurrent = true;
		viewModel
			.loadAida()
			.then(() => {
				if (isCurrent) setUrl(viewModel.quickSightUrl);
			})
			.catch((err: IOperationResultNoValue) => {
				if (isCurrent) setError(`Failed to open QuickSight: ${err.systemMessage}`);
			});
		return () => {
			isCurrent = false;
		};
	}, [dashboardId, userSession, userSession.account.isLevitateSalesCoffeeAccount]);

	if (error) {
		return (
			<p role='alert' className={css(styleSheet.error)}>
				{error}
			</p>
		);
	}

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.frame)}>
				<iframe className={className} style={{ border: 'none', height: '100%', width: '100%' }} src={url} />
			</div>
			{url && (
				<div className={css(styleSheet.linkRow)}>
					<Button variant='link' size='sm' isLoading={openInNewTab.isPending} onClick={() => openInNewTab.mutate()}>
						Open in New Tab
						<OpenNewWindowIcon className={css(styleSheet.linkIcon)} fill='currentColor' />
					</Button>
				</div>
			)}
		</div>
	);
};
